import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Lock, ArrowRight, ShoppingCart, FileText, Mail, FileWarning, Cookie } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_BY_TIPO = {
  privacy: FileText,
  cookies: Cookie,
  checkout: ShoppingCart,
  contact: Mail,
  forms: FileWarning,
};

function redirectToWebpay(url, token) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "token_ws";
  input.value = token;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
}

export default function DeepScanTeaser({ teaser }) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!teaser || !teaser.has_value) return null;

  const pages = teaser.discoverable_pages || [];
  const sampled = teaser.sampled_page;
  const extraTrackers = teaser.sampled_extra_trackers_count || 0;
  const extraForms = teaser.sampled_extra_forms_count || 0;

  const handleActivarPro = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (!r?.data?.id) throw new Error("no-session");
      const { data } = await axios.post(`${API}/pro/checkout/transbank`, {}, { withCredentials: true });
      if (data?.buy_order === "ALREADY_PRO") {
        navigate("/cuenta");
        return;
      }
      if (data?.url && data?.token) {
        redirectToWebpay(data.url, data.token);
        return;
      }
      toast.error("Webpay no devolvió el link. Reintenta.");
    } catch (err) {
      if (err?.message === "no-session" || err?.response?.status === 401) {
        navigate("/registro?checkout=transbank");
        return;
      }
      toast.error(err?.response?.data?.detail || "No pudimos iniciar el pago.");
    } finally {
      setLoading(false);
    }
  };

  // Mensaje principal basado en si encontramos algo concreto en el sample
  let headline;
  let subline;
  if (sampled && (extraTrackers > 0 || extraForms > 0)) {
    const parts = [];
    if (extraTrackers > 0) {
      parts.push(`${extraTrackers} tracker${extraTrackers > 1 ? "s" : ""} adicional${extraTrackers > 1 ? "es" : ""}`);
    }
    if (extraForms > 0) {
      parts.push(`${extraForms} formulario${extraForms > 1 ? "s" : ""} más`);
    }
    headline = `Pro encontraría ${parts.join(" y ")} en tu ${sampled.label.toLowerCase()}`;
    subline = `Detectamos en ${sampled.url} contenido que el scan gratuito no incluye en este reporte. Tienes el riesgo, pero no la visibilidad completa hasta activar Pro.`;
  } else if (pages.length > 0) {
    headline = `Pro analizaría también ${pages.length} ${pages.length === 1 ? "página interna" : "páginas internas"} de tu sitio`;
    subline = "El scan gratuito solo cubre la URL que pegaste. Pro descubre y analiza automáticamente las páginas donde típicamente se cargan pixels y formularios.";
  } else {
    return null;
  }

  return (
    <div
      data-testid="deep-scan-teaser"
      className="mt-8 mb-8 relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 text-white rounded-2xl p-6 sm:p-10 border border-slate-800"
    >
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex items-start gap-4 mb-6">
          <div className="bg-white/10 rounded-xl p-3 shrink-0">
            <Search className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-slate-900 px-2 py-0.5 rounded">
              <Lock className="w-3 h-3" />
              Deep Scan Pro
            </div>
            <h3 className="mt-3 text-xl sm:text-2xl font-bold tracking-tight">
              {headline}
            </h3>
            <p className="mt-2 text-sm text-slate-300 leading-relaxed max-w-2xl">
              {subline}
            </p>
          </div>
        </div>

        {pages.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {pages.slice(0, 4).map((p) => {
              const Icon = ICON_BY_TIPO[p.tipo] || FileText;
              const isSampled = sampled && sampled.url === p.url;
              return (
                <div
                  key={p.url}
                  data-testid={`deep-teaser-page-${p.tipo}`}
                  className="flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3"
                >
                  <Icon className="w-4 h-4 text-blue-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{p.label}</p>
                    <p className="text-[11px] text-slate-400 truncate">{new URL(p.url).pathname || "/"}</p>
                  </div>
                  {isSampled && (extraTrackers > 0 || extraForms > 0) && (
                    <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider bg-amber-400/20 text-amber-300 border border-amber-400/40 px-2 py-0.5 rounded">
                      {extraTrackers + extraForms} oculto{extraTrackers + extraForms > 1 ? "s" : ""}
                    </span>
                  )}
                  <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-4 border-t border-white/10">
          <button
            type="button"
            onClick={handleActivarPro}
            disabled={loading}
            data-testid="deep-teaser-cta"
            className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 text-sm font-semibold px-5 py-3 rounded-full hover:bg-slate-100 disabled:opacity-60 transition-colors"
          >
            {loading ? "Iniciando pago…" : "Activar Pro y ver el análisis completo"}
            <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-slate-400">
            $14.990 CLP · pago único · acceso permanente.
          </p>
        </div>
      </div>
    </div>
  );
}
